import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { AirCfarOffer, AirCfarOfferV2 } from "@b2bportal/air-cfar-api";
import type { FintechMarketplaceProductOfferMeta } from "@b2bportal/core-fintech-ui";
import {
  CallState,
  type ExternalResource,
  type ISelectedTrip,
} from "@b2bportal/core-types";

import { FlightsCfarOffersSelectors } from "../store";
import { toFetchCfarOffersParams } from "../utils";
import { useInitFlightsCfarOffers } from "./useInitFlightsCfar";

export interface InitFintechProductOffersConfig {
  baseProduct: {
    selectedTrip:
      | Pick<ISelectedTrip, "tripId" | "returnFareId" | "outgoingFareId">
      | undefined;
  };
}

export const useInitFintechProductOffers = (
  config: InitFintechProductOffersConfig
) => {
  const { selectedTrip } = config.baseProduct;
  const stableSelectedTrip = useMemo(
    () =>
      selectedTrip
        ? {
            tripId: selectedTrip.tripId,
            returnFareId: selectedTrip.returnFareId,
            outgoingFareId: selectedTrip.outgoingFareId,
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedTrip?.tripId,
      selectedTrip?.returnFareId,
      selectedTrip?.outgoingFareId,
    ]
  );

  const fetchCfarOffersCallState = useSelector(
    FlightsCfarOffersSelectors.getFetchCfarOffersCallState
  );

  const fetchedCfarOffers = useSelector(
    FlightsCfarOffersSelectors.getFetchedCfarOffers
  );

  const currentProductOffersState = useMemo(
    () =>
      toCfarFintechMarketplaceProductOffers(
        fetchCfarOffersCallState,
        fetchedCfarOffers
      ),
    [fetchCfarOffersCallState, fetchedCfarOffers]
  );

  const fetchCfarOffersParams = useMemo(
    () => toFetchCfarOffersParams(stableSelectedTrip),
    [stableSelectedTrip]
  );

  const { fetchOffers } = useInitFlightsCfarOffers({
    currentFlight: fetchCfarOffersParams,
  });

  return [currentProductOffersState, fetchOffers] as const;
};

const toCfarFintechMarketplaceProductOffers = (
  callState: CallState,
  cfarOffers: AirCfarOffer[] | AirCfarOfferV2[]
): ExternalResource<
  Array<FintechMarketplaceProductOfferMeta<(typeof cfarOffers)[0]>>
> => {
  switch (callState) {
    case CallState.NotCalled:
    case CallState.InProcess:
      return {
        state: callState,
      };
    case CallState.Success:
      return {
        state: callState,
        data: cfarOffers.map((offer: AirCfarOffer | AirCfarOfferV2) => ({
          premiumAmount: offer.premiumAmount,
          payload: offer,
        })),
      };
    case CallState.Failed:
      return {
        state: callState,
        error: "",
      };
  }
};
